import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, RefreshCw, Search, Loader2, CheckCircle2, XCircle,
  Clock, FileText, Settings2, Eye,
} from "lucide-react";

const POLICY_LABEL = {
  one_time: "One-time (default)",
  "12_months": "12 months validity",
  per_transaction: "Per-transaction (re-OTP every txn)",
  tiered: "Tiered (amount-based)",
};

const STATUS_META = {
  verified: { tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  pending: { tone: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  rejected: { tone: "bg-rose-50 text-rose-700 border-rose-200", icon: XCircle },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { tone: "bg-zinc-50 text-zinc-700 border-zinc-200", icon: FileText };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${meta.tone}`}>
      <Icon className="h-3 w-3" /> {status}
    </span>
  );
}

export default function AdminCustomerKyc() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const [policy, setPolicy] = useState(null);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [distribution, setDistribution] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search.trim()) params.set("search", search.trim());
      const { data } = await api.get(`/admin/customer-kyc?${params.toString()}`);
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  const loadPolicy = async () => {
    try {
      const { data } = await api.get("/admin/customer-kyc/policy");
      setPolicy(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load policy");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { loadPolicy(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/customer-kyc/verification-distribution");
        setDistribution(data);
      } catch { /* tile is optional — non-blocking */ }
    })();
  }, []);

  const savePolicy = async () => {
    if (!policy) return;
    setSavingPolicy(true);
    try {
      const body = {
        mode: policy.mode,
        require_admin_approval: !!policy.require_admin_approval,
        validate_pan_via_api: !!policy.validate_pan_via_api,
        validate_aadhaar_via_api: !!policy.validate_aadhaar_via_api,
        tier_thresholds: policy.tier_thresholds,
      };
      const { data } = await api.patch("/admin/customer-kyc/policy", body);
      setPolicy(data);
      toast.success("Policy saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save policy");
    } finally { setSavingPolicy(false); }
  };

  const approve = async (mobile) => {
    try {
      await api.post(`/admin/customer-kyc/${mobile}/approve`);
      toast.success("Approved");
      setPicked(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const reject = async (mobile) => {
    if (rejectReason.trim().length < 2) { toast.error("Rejection reason required"); return; }
    try {
      await api.post(`/admin/customer-kyc/${mobile}/reject`, { reason: rejectReason.trim() });
      toast.success("Rejected");
      setPicked(null);
      setRejectReason("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Compliance</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 flex items-center gap-2" data-testid="page-title">
            <ShieldCheck className="h-7 w-7 text-primary" strokeWidth={1.5} /> Customer KYC
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            System-level customer KYC records (one record per mobile · shared across all partners). Manage policy validity here too.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Policy panel */}
      <div className="mt-6 surface-card p-5" data-testid="policy-panel">
        <div className="flex items-center gap-2 mb-4">
          <Settings2 className="h-5 w-5 text-primary" strokeWidth={1.5} />
          <span className="font-display font-semibold">KYC validity policy</span>
        </div>
        {policy ? (
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Mode</Label>
              <Select value={policy.mode} onValueChange={(v) => setPolicy({ ...policy, mode: v })}>
                <SelectTrigger className="mt-1" data-testid="policy-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(POLICY_LABEL).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`policy-mode-${k}`}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 sm:pt-7">
              <Switch
                checked={!!policy.require_admin_approval}
                onCheckedChange={(v) => setPolicy({ ...policy, require_admin_approval: v })}
                data-testid="policy-admin-approval"
              />
              <Label className="cursor-pointer">Require admin approval before customer can transact</Label>
            </div>
            {policy.mode === "tiered" && (
              <>
                <div>
                  <Label>No-KYC max amount (₹) <span className="text-muted-foreground text-[10px]">≤ this: skip KYC</span></Label>
                  <Input
                    type="number"
                    value={policy.tier_thresholds?.no_kyc_max ?? 0}
                    onChange={(e) => setPolicy({ ...policy, tier_thresholds: { ...policy.tier_thresholds, no_kyc_max: parseFloat(e.target.value || "0") } })}
                    data-testid="policy-no-kyc-max"
                    className="font-mono mt-1"
                  />
                </div>
                <div>
                  <Label>OTP-only max amount (₹) <span className="text-muted-foreground text-[10px]">&gt; this: full PAN+Aadhaar</span></Label>
                  <Input
                    type="number"
                    value={policy.tier_thresholds?.otp_only_max ?? 5000}
                    onChange={(e) => setPolicy({ ...policy, tier_thresholds: { ...policy.tier_thresholds, otp_only_max: parseFloat(e.target.value || "0") } })}
                    data-testid="policy-otp-only-max"
                    className="font-mono mt-1"
                  />
                </div>
              </>
            )}
            <div className="sm:col-span-2 flex items-center justify-end">
              <Button onClick={savePolicy} disabled={savingPolicy} data-testid="save-policy">
                {savingPolicy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Settings2 className="h-4 w-4 mr-2" />}
                Save policy
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 inline mr-2 animate-spin" /> Loading…</div>
        )}
      </div>

      {/* Parent-system verification % distribution tile */}
      {distribution && distribution.total_customers > 0 && (
        <div className="mt-6 surface-card p-4" data-testid="kyc-distribution">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-display font-semibold text-sm">Parent-system verification distribution</h2>
              <p className="text-xs text-muted-foreground">
                {distribution.total_customers} customer{distribution.total_customers !== 1 ? "s" : ""} · {distribution.verified} verified
              </p>
            </div>
            <div className="flex items-center gap-2">
              {Object.entries(distribution.by_source || {}).map(([src, n]) => (
                <Badge key={src} variant="outline" className="font-mono text-[10px]" data-testid={`source-${src}`}>
                  {src}: <span className="ml-1 font-bold">{n}</span>
                </Badge>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              { key: "100", label: "100%", tone: "emerald" },
              { key: "75_99", label: "75-99%", tone: "lime" },
              { key: "50_74", label: "50-74%", tone: "amber" },
              { key: "below_50", label: "< 50%", tone: "rose" },
              { key: "missing", label: "Missing", tone: "slate" },
            ].map((b) => {
              const n = distribution.verification_buckets[b.key] || 0;
              const pct = distribution.total_customers > 0
                ? Math.round((n / distribution.total_customers) * 100)
                : 0;
              const colors = {
                emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
                lime: "border-lime-200 bg-lime-50 text-lime-700",
                amber: "border-amber-200 bg-amber-50 text-amber-700",
                rose: "border-rose-200 bg-rose-50 text-rose-700",
                slate: "border-slate-200 bg-slate-50 text-slate-600",
              }[b.tone];
              return (
                <div key={b.key} className={`rounded-md border p-3 ${colors}`} data-testid={`bucket-${b.key}`}>
                  <div className="text-[10px] font-medium uppercase tracking-wider opacity-70">{b.label}</div>
                  <div className="font-display font-bold text-2xl mt-1">{n}</div>
                  <div className="text-[10px] opacity-70">{pct}% of total</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="mt-6 surface-card p-4 flex flex-wrap items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="kyc-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search mobile, PAN, name"
            className="pl-8 w-72"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          {["pending", "verified", "rejected"].map((s) => (
            <Badge key={s} variant="outline" className="font-mono text-[11px]" data-testid={`count-${s}`}>
              {s}: <span className="ml-1 font-bold">{counts[s] || 0}</span>
            </Badge>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="mt-4 surface-card overflow-hidden">
        {loading && items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <ShieldCheck className="h-8 w-8 mx-auto mb-3 opacity-50" />
            No KYC records yet.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="kyc-table">
            <thead className="bg-accent/50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                <th className="px-4 py-2 font-medium">Mobile</th>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">PAN</th>
                <th className="px-4 py-2 font-medium">Aadhaar</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Submitted</th>
                <th className="px-4 py-2 font-medium w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-accent/30" data-testid={`kyc-row-${it.mobile}`}>
                  <td className="px-4 py-2.5 font-mono">{it.mobile}</td>
                  <td className="px-4 py-2.5">{it.full_name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{it.pan_number || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">XXXX-XXXX-{it.aadhaar_last4 || "—"}</td>
                  <td className="px-4 py-2.5"><StatusPill status={it.status} /></td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{it.initiated_at ? new Date(it.initiated_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-2.5">
                    <Button variant="ghost" size="sm" onClick={() => setPicked(it)} data-testid={`view-${it.mobile}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail / approve / reject modal */}
      <Dialog open={!!picked} onOpenChange={(o) => { if (!o) { setPicked(null); setRejectReason(""); } }}>
        <DialogContent className="max-w-xl" data-testid="kyc-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" strokeWidth={1.5} />
              {picked?.full_name} <StatusPill status={picked?.status || ""} />
            </DialogTitle>
          </DialogHeader>
          {picked && (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-muted-foreground">Mobile:</span> <code>+91 {picked.mobile}</code></div>
                <div><span className="text-muted-foreground">DOB:</span> <code>{picked.dob}</code></div>
                <div><span className="text-muted-foreground">PAN:</span> <code>{picked.pan_number}</code></div>
                <div><span className="text-muted-foreground">PAN name:</span> <code>{picked.pan_name}</code></div>
                <div><span className="text-muted-foreground">Aadhaar:</span> <code>XXXX-XXXX-{picked.aadhaar_last4}</code></div>
                <div><span className="text-muted-foreground">Submitted:</span> <code>{picked.initiated_at}</code></div>
              </div>
              {picked.address && <div><span className="text-muted-foreground text-xs">Address:</span> {picked.address}</div>}
              {picked.rejection_reason && (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
                  Previous rejection: {picked.rejection_reason}
                </div>
              )}
              {picked.status === "pending" && (
                <div>
                  <Label>Rejection reason (required if rejecting)</Label>
                  <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="mt-1" data-testid="reject-reason" />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            {picked?.status !== "verified" && (
              <Button onClick={() => approve(picked.mobile)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn">
                <CheckCircle2 className="h-4 w-4 mr-2" /> Approve
              </Button>
            )}
            {picked?.status !== "rejected" && (
              <Button variant="destructive" onClick={() => reject(picked.mobile)} disabled={!rejectReason.trim()} data-testid="reject-btn">
                <XCircle className="h-4 w-4 mr-2" /> Reject
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
